import { ImpersonationTokens, superAdminApi } from "./authService";
import { Plan, PlanCurrency } from "./planService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const RESOURCE = "/super-admin/tenants";

export type TenantSubscriptionStatus =
  | "active"
  | "past_due"
  | "suspended"
  | "canceled";

export type TenantBlockedFilter = "all" | "blocked" | "operational";

export type TenantListSort = "created_at" | "name" | "last_login_at";

export type TenantListOrder = "asc" | "desc";

/** Latest subscription returned with tenant list/detail (GET /super-admin/tenants). */
export interface TenantSubscription {
  id: number;
  tenant_id: number;
  plan_id: number;
  status: TenantSubscriptionStatus;
  currency: PlanCurrency;
  current_period_start: string;
  current_period_end: string;
  external_subscription_id?: string | null;
  gateway_slug?: string | null;
  created_at: string;
  updated_at: string;
  plan?: Plan | null;
}

/** Owner user linked to a tenant (GET /super-admin/tenants). */
export interface OwnerUser {
  id: number;
  name: string;
  email: string;
}

export interface TenantSettingsSummary {
  locale: string;
  timezone: string;
  currency_code: string;
}

export interface TenantActivationSummary {
  trial_started_at: string | null;
  trial_ends_at: string | null;
  aha_completed_at: string | null;
  concierge_completed_at: string | null;
  nurture_opt_out?: boolean;
  nurture_opted_out_at?: string | null;
}

/** Tenant entity returned by GET /super-admin/tenants. */
export interface TenantBlockAudit {
  super_admin_id: number | null;
  super_admin_name: string | null;
  blocked_at: string | null;
  block_reason: string | null;
}

export interface Tenant {
  id: number;
  name: string;
  document: string;
  partner_id?: number | null;
  public_slug?: string | null;
  blocked_at: string | null;
  block_reason: string | null;
  block_audit?: TenantBlockAudit | null;
  is_demo?: boolean;
  courtesy_until?: string | null;
  units_count: number;
  professionals_count: number;
  owner_user: OwnerUser | null;
  subscription: TenantSubscription | null;
  last_login_at: string | null;
  settings_summary?: TenantSettingsSummary | null;
  activation_summary?: TenantActivationSummary | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateTenantSubscriptionPayload {
  extend_days?: number;
  status?: TenantSubscriptionStatus;
  current_period_end?: string;
  is_demo?: boolean;
  courtesy_until?: string | null;
  clear_courtesy?: boolean;
  plan_id?: number;
  reason?: string;
}

export interface TenantListParams extends PaginationParams {
  search?: string;
  subscription_status?: TenantSubscriptionStatus;
  blocked?: boolean;
  plan_id?: number;
  sort?: TenantListSort;
  order?: TenantListOrder;
}

export const getTenants = async (
  params?: TenantListParams
): Promise<PaginatedApiResponse<Tenant>> => {
  const response = await superAdminApi.get<PaginatedApiResponse<Tenant>>(
    RESOURCE,
    { params }
  );

  return response.data;
};

export const getTenant = async (id: number): Promise<ApiResponse<Tenant>> => {
  const response = await superAdminApi.get<ApiResponse<Tenant>>(
    `${RESOURCE}/${id}`
  );

  return response.data;
};

export interface CreateTenantPayload {
  studio_name: string;
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  plan_id: number;
  locale?: "pt-BR" | "en" | "es";
  currency_code?: PlanCurrency;
  coupon_code?: string;
}

export const createTenant = async (
  payload: CreateTenantPayload
): Promise<ApiResponse<Tenant>> => {
  const response = await superAdminApi.post<ApiResponse<Tenant>>(
    RESOURCE,
    payload
  );

  return response.data;
};

/**
 * Toggles manual block on a tenant.
 * - To block: pass `blockReason` (required by API when `blocked_at` is null).
 * - To unblock: omit `blockReason` (PATCH with empty body).
 */
export const toggleTenantBlock = async (
  id: number,
  blockReason?: string
): Promise<ApiResponse<Tenant>> => {
  const body =
    blockReason !== undefined ? { block_reason: blockReason } : {};

  const response = await superAdminApi.patch<ApiResponse<Tenant>>(
    `${RESOURCE}/${id}/block`,
    body
  );

  return response.data;
};

export const updateTenantSubscription = async (
  id: number,
  payload: UpdateTenantSubscriptionPayload
): Promise<ApiResponse<Tenant>> => {
  const response = await superAdminApi.patch<ApiResponse<Tenant>>(
    `${RESOURCE}/${id}/subscription`,
    payload
  );

  return response.data;
};

export interface SyncTenantSubscriptionPaymentResponse
  extends ApiResponse<Tenant> {
  meta?: {
    activated?: boolean;
  };
}

export const syncTenantSubscriptionPayment = async (
  id: number,
  payload?: { reason?: string }
): Promise<SyncTenantSubscriptionPaymentResponse> => {
  const response = await superAdminApi.post<SyncTenantSubscriptionPaymentResponse>(
    `${RESOURCE}/${id}/subscription/sync-payment`,
    payload ?? {}
  );

  return response.data;
};

export const impersonate = async (
  userId: number
): Promise<ApiResponse<ImpersonationTokens>> => {
  const response = await superAdminApi.post<ApiResponse<ImpersonationTokens>>(
    `/super-admin/impersonate/${userId}`
  );

  return response.data;
};
